import { DateTime } from 'luxon';
import { SALES_ALL_BUILDINGS } from './sales';

/**
 * t554u: Store sales (Home › Store sales on a computer, the Store sales card on the phone Dashboard).
 *
 * Everything here comes from the till sync (docs/odoo-sales.md): workforce.sales_hourly (takings net of refunds incl.
 * VAT, transactions, refunds, refund value, items) and workforce.sales_products_daily (per product per day). Odoo
 * categories, payment methods, cashiers and tills aren't synced, so they aren't shown.
 *
 * Periods are whole days on the organisation's clock (workforce.settings.timezone, Europe/London by default). A period
 * that is still going (Today, This week, This month, or a custom range reaching today) is compared with the same
 * stretch of the previous one: up to the same clock time, counting the hour that holds the cut-off pro rata.
 */
export type SalesReportPeriod = 'today' | 'yesterday' | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'custom';
export const SALES_REPORT_PERIODS: SalesReportPeriod[] = ['today', 'yesterday', 'this-week', 'last-week', 'this-month', 'last-month', 'custom'];
/** The phone keeps it simple. */
export const SALES_PHONE_PERIODS: SalesReportPeriod[] = ['today', 'this-week', 'this-month'];
export const SALES_PERIOD_LABEL: Record<SalesReportPeriod, string> = {
  today: 'Today', yesterday: 'Yesterday', 'this-week': 'This week', 'last-week': 'Last week', 'this-month': 'This month', 'last-month': 'Last month', custom: 'Custom range',
};
/** Longest custom range (days). */
export const SALES_REPORT_MAX_DAYS = 366;
/** `building` value for every building I can see sales at, added together. Matches the widgets' switch (t553u). */
export const SALES_SCOPE_ALL = SALES_ALL_BUILDINGS;

export interface SalesReportRange {
  period: SalesReportPeriod;
  /** Inclusive local days. */
  from: string; to: string;
  /** UTC instants: hours starting in [start, end) count. */
  start: string; end: string;
  compare_from: string; compare_to: string;
  compare_start: string;
  /** Hours before compare_end count; the hour holding it counts pro rata (only when partial). */
  compare_end: string;
  /** "last week", "this time yesterday"… for "▲ 12% on last week". */
  compare_label: string;
  /** The period is still going: figures run to `end` (the last sync, or now). */
  partial: boolean;
  days: number;
  timezone: string;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
export class SalesRangeError extends Error {}

/**
 * The days and instants a period covers at `now` on the organisation's clock, and the previous equivalent period.
 * `asOf` (the last successful sync) caps a period that is still going, so "so far" means the same on both sides.
 */
export function salesReportRange(period: SalesReportPeriod, now: Date, tz: string, custom?: { from?: string | null; to?: string | null }, asOf?: Date | null): SalesReportRange {
  const n = DateTime.fromJSDate(now).setZone(tz);
  if (!n.isValid) throw new SalesRangeError('Unknown time zone');
  const today = n.startOf('day');
  let start: DateTime; let endDay: DateTime; let shift: { days?: number; weeks?: number; months?: number }; let label: string;
  switch (period) {
    case 'today': start = today; endDay = today; shift = { days: 1 }; label = 'this time yesterday'; break;
    case 'yesterday': start = today.minus({ days: 1 }); endDay = start; shift = { days: 1 }; label = 'the day before'; break;
    case 'this-week': start = today.startOf('week'); endDay = today.endOf('week').startOf('day'); shift = { weeks: 1 }; label = 'last week'; break;
    case 'last-week': start = today.minus({ weeks: 1 }).startOf('week'); endDay = start.plus({ days: 6 }); shift = { weeks: 1 }; label = 'the week before'; break;
    case 'this-month': start = today.startOf('month'); endDay = today.endOf('month').startOf('day'); shift = { months: 1 }; label = 'last month'; break;
    case 'last-month': start = today.minus({ months: 1 }).startOf('month'); endDay = start.endOf('month').startOf('day'); shift = { months: 1 }; label = 'the month before'; break;
    case 'custom': {
      const f = custom?.from, t = custom?.to;
      if (!f || !t || !ISO_DAY.test(f) || !ISO_DAY.test(t)) throw new SalesRangeError('Pick a start and end date');
      const a = DateTime.fromISO(f, { zone: tz }), b = DateTime.fromISO(t, { zone: tz });
      if (!a.isValid || !b.isValid) throw new SalesRangeError('Pick a start and end date');
      [start, endDay] = a <= b ? [a.startOf('day'), b.startOf('day')] : [b.startOf('day'), a.startOf('day')];
      const days = Math.round(endDay.diff(start, 'days').days) + 1;
      if (days > SALES_REPORT_MAX_DAYS) throw new SalesRangeError(`Pick a range of up to ${SALES_REPORT_MAX_DAYS} days`);
      shift = { days };
      label = days === 1 ? 'the day before' : `the previous ${days} days`;
      break;
    }
    default: throw new SalesRangeError('Unknown period');
  }
  const fullEnd = endDay.plus({ days: 1 }).startOf('day');
  const partial = start <= n && n < fullEnd;
  // Still going: up to the last sync (if it is inside the period and not in the future), else now.
  const a = asOf ? DateTime.fromJSDate(asOf).setZone(tz) : null;
  const cut = partial ? (a && a >= start && a <= n ? a : n) : fullEnd;
  const cStart = start.minus(shift);
  // Luxon keeps the wall-clock time across a clock change, so "this time last week" is the same local time.
  const cEnd = partial ? cut.minus(shift) : cStart.plus(shift).startOf('day');
  const compareEndDay = (partial ? cEnd : cEnd.minus({ days: 1 })).startOf('day');
  return {
    period, from: start.toISODate()!, to: endDay.toISODate()!,
    start: start.toUTC().toISO()!, end: cut.toUTC().toISO()!,
    compare_from: cStart.toISODate()!, compare_to: compareEndDay < cStart ? cStart.toISODate()! : compareEndDay.toISODate()!,
    compare_start: cStart.toUTC().toISO()!, compare_end: cEnd.toUTC().toISO()!,
    compare_label: label, partial, days: Math.round(endDay.diff(start, 'days').days) + 1, timezone: tz,
  };
}

// ------------------------------------------------------------------------------------------------ API shape
export interface SalesReportFigures {
  /** Takings incl. VAT with refunds netted off (the same basis as targets). */
  sales_pence: number;
  /** Before refunds: sales + refund value. */
  gross_pence: number;
  transactions: number;
  atv_pence: number | null;
  items: number;
  refunds: number;
  refunds_pence: number;
}
export interface SalesReportBuilding { location_id: string; location_name: string; current: SalesReportFigures; previous: SalesReportFigures }
export interface SalesReportProduct { product_ref: string; product_name: string; qty: number; sales_pence: number }
export interface SalesReport {
  /** 'all' or the building's id. */
  building: string;
  /** Buildings I can pick (with till sales); the switch shows only with two or more. */
  buildings: { id: string; name: string }[];
  range: SalesReportRange;
  as_of: string | null;
  current: SalesReportFigures;
  previous: SalesReportFigures;
  /** Every day of the period (future days of a period still going are left out), with the previous period's day at the same position. */
  daily: { date: string; sales_pence: number; transactions: number; previous_date: string | null; previous_sales_pence: number | null }[];
  /** Local hour of day (0-23) over the period; hours with nothing are left out. */
  hourly: { hour: number; sales_pence: number; transactions: number }[];
  /** Each building and its figures, when it's All buildings and I can see two or more. */
  by_building: SalesReportBuilding[] | null;
  /** Top 10 by sales and top 10 by quantity (whole days of the period). */
  top_by_sales: SalesReportProduct[];
  top_by_qty: SalesReportProduct[];
  products: number;
}

// ------------------------------------------------------------------------------------------------ helpers
export const emptySalesFigures = (): SalesReportFigures => ({ sales_pence: 0, gross_pence: 0, transactions: 0, atv_pence: null, items: 0, refunds: 0, refunds_pence: 0 });

/** Builds figures from summed columns (ATV from the totals, never an average of averages). */
export function salesFigures(v: { sales_pence: number; transactions: number; items: number; refunds: number; refunds_pence: number }): SalesReportFigures {
  const sales = Math.round(v.sales_pence), tx = Math.round(v.transactions), refundsPence = Math.round(v.refunds_pence);
  return { sales_pence: sales, gross_pence: sales + refundsPence, transactions: tx, atv_pence: tx > 0 ? Math.round(sales / tx) : null,
    items: Math.round(v.items * 100) / 100, refunds: Math.round(v.refunds), refunds_pence: refundsPence };
}
export function addSalesFigures(list: readonly SalesReportFigures[]): SalesReportFigures {
  return salesFigures(list.reduce((a, f) => ({ sales_pence: a.sales_pence + f.sales_pence, transactions: a.transactions + f.transactions, items: a.items + f.items, refunds: a.refunds + f.refunds, refunds_pence: a.refunds_pence + f.refunds_pence }),
    { sales_pence: 0, transactions: 0, items: 0, refunds: 0, refunds_pence: 0 }));
}

/** Plain change text: "▲ 12% on last week", "▼ 3% on last week", "Same as last week", "None on last week". */
export function salesChangeText(cur: number | null | undefined, prev: number | null | undefined, label: string): string {
  if (prev == null || prev === 0) return cur ? `Nothing to compare with ${label}` : `Same as ${label}`;
  if (cur == null) return '';
  const pct = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  if (pct === 0) return `Same as ${label}`;
  return `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}% on ${label}`;
}

/** The bare change for a table cell: "▲ 12%", "▼ 3%", "Same", or "-" when there's nothing to compare with. */
/** Phones: "this time yesterday" reads as "yesterday" so the change line fits under a figure ("▼ 3% on yesterday"). */
export const salesComparePhoneLabel = (label: string) => (label === 'this time yesterday' ? 'yesterday' : label);

export function salesChangeShort(cur: number | null | undefined, prev: number | null | undefined): string {
  if (prev == null || prev === 0) return cur ? '-' : 'Same';
  if (cur == null) return '-';
  const pct = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  return pct === 0 ? 'Same' : `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}%`;
}

/** Items per transaction, one decimal. */
export const itemsPerTransaction = (f: Pick<SalesReportFigures, 'items' | 'transactions'>) => (f.transactions > 0 ? Math.round((f.items / f.transactions) * 10) / 10 : null);

export type SalesBuildingSort = 'name' | 'sales' | 'change' | 'share' | 'transactions' | 'atv' | 'items' | 'refunds';
/** Sorts the building table (the total row is separate). Text ascending; numbers descending by default. */
export function sortSalesBuildings(rows: readonly SalesReportBuilding[], key: SalesBuildingSort, dir: 'asc' | 'desc'): SalesReportBuilding[] {
  const val = (r: SalesReportBuilding): number | string => {
    switch (key) {
      case 'name': return r.location_name.toLocaleLowerCase('en-GB');
      case 'sales': case 'share': return r.current.sales_pence;
      case 'change': return r.previous.sales_pence > 0 ? (r.current.sales_pence - r.previous.sales_pence) / r.previous.sales_pence : Number.NEGATIVE_INFINITY;
      case 'transactions': return r.current.transactions;
      case 'atv': return r.current.atv_pence ?? -1;
      case 'items': return r.current.items;
      case 'refunds': return r.current.refunds_pence;
    }
  };
  const m = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = val(a), y = val(b);
    const c = typeof x === 'string' ? x.localeCompare(y as string, 'en-GB', { numeric: true }) : (x as number) - (y as number);
    return c * m || a.location_name.localeCompare(b.location_name, 'en-GB');
  });
}

/** Where the page's building choice is remembered on this device (per workspace and person). */
export const salesReportPickKey = (ws: string, userId: string) => `hub.sales.report.building:${ws}:${userId}`;
/**
 * t584u: the phone Dashboard's Store sales card (iOS and phone web) keeps its own memory, so it starts on All buildings
 * (a building narrowed to on the desktop page, or by tapping a building row, no longer carries over). Only a building
 * picked in the card's building menu is remembered.
 */
export const salesDashboardPickKey = (ws: string, userId: string) => `hub.sales.dashboard.building:${ws}:${userId}`;
export const salesReportPeriodKey = (ws: string, userId: string, phone = false) => `hub.sales.report.period:${ws}:${userId}${phone ? ':phone' : ''}`;

/** The building to ask for: a remembered one I can still see, else All buildings (or my only building). */
export function salesReportChoice(options: readonly { id: string }[] | null | undefined, picked: string | null | undefined): string {
  if (options && options.length === 1) return options[0].id;
  if (!picked) return SALES_SCOPE_ALL;
  if (!options) return picked;
  return picked === SALES_SCOPE_ALL || options.some(o => o.id === picked) ? picked : SALES_SCOPE_ALL;
}

/** "12:00" hour label. */
export const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;
